import { apiGet } from "./apiClient";

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.GOOGLE_MAPS_API_KEY;

// Resolved photo URLs are stable; 30 min keeps them out of the network path for
// a whole session without pinning stale data across a deploy.
const PHOTO_TTL_MS = 30 * 60 * 1000;

// This module logged ~20 lines per image resolution, which on a 40-card grid is
// hundreds of console writes competing with render work. Kept for local debugging.
const DEBUG = process.env.NODE_ENV !== "production";
const debugLog = (...args) => {
  // eslint-disable-next-line no-console
  if (DEBUG) console.warn(...args);
};

let mapsLoadPromise = null;
// Fast in-memory layer in front of apiGet's sessionStorage cache. Bounded so a
// long browsing session can't grow it without limit.
const MAX_MEMO_ENTRIES = 400;
const imageUrlCache = new Map();
const inFlightRequests = new Map();

/** Insertion-ordered eviction — drops the oldest entry once the cap is hit. */
const memoize = (key, value) => {
  if (!imageUrlCache.has(key) && imageUrlCache.size >= MAX_MEMO_ENTRIES) {
    imageUrlCache.delete(imageUrlCache.keys().next().value);
  }
  imageUrlCache.set(key, value);
};

const normalizeKey = (value = "") =>
  String(value).trim().toLowerCase().replace(/\s+/g, " ");

const buildQuery = ({ placeName = "", district = "", stateName = "" }) => {
  const primary = [placeName, district, stateName, "India"].filter(Boolean).join(" ");
  const fallback = [placeName, stateName, "India"].filter(Boolean).join(" ");

  return {
    primary,
    fallback,
  };
};

const loadGooglePlacesLibrary = async () => {
  if (typeof window === "undefined") return null;

  if (window.google?.maps?.places?.Place) {
    return window.google.maps;
  }

  if (mapsLoadPromise) {
    return mapsLoadPromise;
  }

  mapsLoadPromise = (async () => {
    if (!GOOGLE_MAPS_API_KEY) {
      throw new Error("Missing Google Maps API key");
    }

    const existingScript = document.querySelector('script[data-google-maps="true"]');
    
    if (!window.google?.maps) {
      debugLog(`[loadGooglePlacesLibrary] Loading Google Maps script (v=weekly)...`);
      await new Promise((resolve, reject) => {
        if (existingScript) {
          existingScript.addEventListener("load", resolve);
          existingScript.addEventListener("error", reject);
        } else {
          const script = document.createElement("script");
          script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=weekly&libraries=places`;
          script.async = true;
          script.defer = true;
          script.dataset.googleMaps = "true";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        }
      });
    }

    if (window.google?.maps?.importLibrary) {
      debugLog(`[loadGooglePlacesLibrary] Importing 'places' library...`);
      const placesLib = await window.google.maps.importLibrary("places");
      debugLog(`[loadGooglePlacesLibrary] 'places' library loaded. Place class available:`, !!placesLib?.Place);
      return window.google.maps;
    }

    return window.google?.maps || null;
  })();

  return mapsLoadPromise;
};

const textSearchPhoto = async (maps, query, maxWidth) => {
  try {
    debugLog(`[textSearchPhoto] Searching for: "${query}"`);
    // Try to find the Place class in different possible locations
    const Place = maps.places?.Place || window.google?.maps?.places?.Place;
    
    let useClassicFallback = false;
    if (Place?.searchByText) {
      debugLog(`[textSearchPhoto] Using modern Place.searchByText`);
      const request = {
        textQuery: query,
        fields: ["photos", "id", "displayName"],
        maxResultCount: 1,
      };
      
      try {
        const searchPromise = Place.searchByText(request);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Place.searchByText call timed out after 3000ms")), 3000)
        );

        const { places } = await Promise.race([searchPromise, timeoutPromise]);
        debugLog(`[textSearchPhoto] Result count: ${places?.length || 0}`);
        
        if (places?.[0]?.photos?.length > 0) {
          const urls = places[0].photos.slice(0, 10).map(p => p.getURI({ maxWidth }));
          debugLog(`[textSearchPhoto] Found ${urls.length} photo URIs`);
          return urls;
        }
        return [];
      } catch (searchErr) {
        debugLog(`[textSearchPhoto] Place.searchByText threw error or timed out, falling back to classic PlacesService:`, searchErr);
        useClassicFallback = true;
      }
    }

    if (!Place?.searchByText || useClassicFallback) {
      return new Promise((resolve) => {
        try {
          const holder = document.createElement("div");
          const service = new maps.places.PlacesService(holder);

          service.textSearch({ query }, (results, status) => {
            if (
              status === maps.places.PlacesServiceStatus.OK &&
              results?.[0]?.photos?.length > 0
            ) {
              const urls = results[0].photos.slice(0, 10).map(p => p.getUrl({ maxWidth }));
              resolve(urls);
              return;
            }
            resolve([]);
          });
        } catch {
          resolve([]);
        }
      });
    }
  } catch (err) {
    debugLog("[getGooglePlaceImage] textSearchPhoto error:", err);
    return null;
  }
};

export const getGooglePlaceImage = async ({
  placeName,
  district,
  stateName,
  maxWidth = 800,
}) => {
  if (!placeName) return null;

  const cacheKey = normalizeKey(`${placeName}|${district}|${stateName}|${maxWidth}`);
  if (imageUrlCache.has(cacheKey)) {
    return imageUrlCache.get(cacheKey);
  }

  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const requestPromise = (async () => {
    try {
      debugLog(`[getGooglePlaceImage] Starting fetch for: "${placeName}" in ${district}, ${stateName}`);
      // First try server-side resolver.
      // Routed through apiGet so the result is persisted in sessionStorage:
      // imageUrlCache below is a module-level Map, which is fast but dies on
      // every reload or restored tab, so photos were re-resolved from scratch
      // far more often than the in-memory hit rate suggested.
      try {
        const jd = await apiGet("api/google-place-photo", {
          params: {
            place: placeName,
            district: district || "",
            state: stateName || "",
            maxwidth: maxWidth,
          },
          ttl: PHOTO_TTL_MS,
        });

        if (jd?.photoUrls && jd.photoUrls.length > 0) {
          memoize(cacheKey, jd.photoUrls);
          return jd.photoUrls;
        }
      } catch (e) {
        debugLog(`[getGooglePlaceImage] Backend resolver failed:`, e.message);
      }

      debugLog(`[getGooglePlaceImage] Falling back to client-side Google Maps SDK...`);
      const maps = await loadGooglePlacesLibrary();
      if (!maps) {
        debugLog(`[getGooglePlaceImage] ❌ Failed to load Google Maps Library`);
        return null;
      }

      const { primary, fallback } = buildQuery({ placeName, district, stateName });
      debugLog(`[getGooglePlaceImage] Client search - Primary: "${primary}", Fallback: "${fallback}"`);
      
      const firstTry = await textSearchPhoto(maps, primary, maxWidth);
      if (firstTry && firstTry.length > 0) {
        debugLog(`[getGooglePlaceImage] ✅ Success from client (primary): ${firstTry.length} urls`);
        memoize(cacheKey, firstTry);
        return firstTry;
      }

      if (fallback && fallback !== primary) {
        const secondTry = await textSearchPhoto(maps, fallback, maxWidth);
        if (secondTry && secondTry.length > 0) {
          debugLog(`[getGooglePlaceImage] ✅ Success from client (fallback): ${secondTry.length} urls`);
          memoize(cacheKey, secondTry);
          return secondTry;
        }
      }

      debugLog(`[getGooglePlaceImage] ❌ No results from Google for "${placeName}"`);
      memoize(cacheKey, []);
      return [];
    } catch {
      memoize(cacheKey, []);
      return [];
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();

  inFlightRequests.set(cacheKey, requestPromise);
  return requestPromise;
};
